// PwaInstall.tsx — kehoite asentaa Savroc ERP aloitusnäytölle.
//
// Chromium-pohjaiset selaimet (Chrome, Edge, Samsung Internet) lähettävät
// `beforeinstallprompt`-eventin → näytetään natiivi asennuskehoite.
// iOS Safari ei tue eventtiä — näytetään ohjeellinen banneri kuvalla
// jakovalikon → "Lisää aloitusnäyttöön".
//
// Banneri näytetään max 1× / 7 vrk per käyttäjä (localStorage-flag).
// Service workerin rekisteröi vite-plugin-pwa, ei tämä komponentti.

import { useEffect, useState } from 'react';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const DISMISS_KEY = 'pwa_install_dismissed';
const DISMISS_TTL = 7 * 86400_000; // 7 vrk

function isStandalone(): boolean {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (navigator as any).standalone === true
  );
}

function isIos(): boolean {
  const ua = navigator.userAgent.toLowerCase();
  return /iphone|ipad|ipod/.test(ua) && !/crios|fxios|edgios/.test(ua);
}

export default function PwaInstall() {
  const [installEvent, setInstallEvent] = useState<BeforeInstallPromptEvent | null>(null);
  const [show, setShow] = useState(false);
  const [iosHint, setIosHint] = useState(false);

  useEffect(() => {
    if (isStandalone()) return; // jo asennettu — älä näytä mitään

    const dismissedAt = localStorage.getItem(DISMISS_KEY);
    if (dismissedAt && Date.now() - Number(dismissedAt) < DISMISS_TTL) return;

    // Chromium-flow: beforeinstallprompt-event
    const handler = (e: Event) => {
      e.preventDefault();
      setInstallEvent(e as BeforeInstallPromptEvent);
      setShow(true);
    };
    window.addEventListener('beforeinstallprompt', handler);

    // iOS-flow: ei eventtiä → näytä ohjeellinen banneri 3 sek viiveellä
    let iosTimer: ReturnType<typeof setTimeout> | null = null;
    if (isIos()) {
      iosTimer = setTimeout(() => setIosHint(true), 3000);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      if (iosTimer) clearTimeout(iosTimer);
    };
  }, []);

  async function install() {
    if (!installEvent) return;
    await installEvent.prompt();
    setShow(false);
    setInstallEvent(null);
  }

  function dismiss() {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setShow(false);
    setIosHint(false);
  }

  // Chromium: beforeinstallprompt-pohjainen kehoite
  if (show && installEvent) {
    return (
      <div style={bannerStyle}>
        <div style={{ flex: 1 }}>
          <strong style={{ fontSize: 13, color: '#0f1923' }}>Asenna Savroc aloitusnäytölle</strong>
          <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>
            Toimii kuin sovellus, nopeampi käyttö
          </div>
        </div>
        <button onClick={dismiss} style={btnGhost}>Ei nyt</button>
        <button onClick={install} style={btnPrimary}>Asenna</button>
      </div>
    );
  }

  // iOS Safari: ohjeellinen banneri
  if (iosHint) {
    return (
      <div style={bannerStyle}>
        <div style={{ flex: 1 }}>
          <strong style={{ fontSize: 13, color: '#0f1923' }}>Lisää Savroc aloitusnäytölle</strong>
          <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>
            Paina <span style={{ fontSize: 14 }}>⬆️</span> jakovalikkoa → "Lisää aloitusnäyttöön"
          </div>
        </div>
        <button onClick={dismiss} style={btnGhost}>OK</button>
      </div>
    );
  }

  return null;
}

const bannerStyle: React.CSSProperties = {
  position: 'fixed',
  bottom: 'calc(16px + env(safe-area-inset-bottom, 0px))',
  left: 16,
  right: 16,
  zIndex: 999,
  background: '#ffffff',
  border: '1px solid #e2e8f0',
  borderRadius: 12,
  padding: '12px 14px',
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  boxShadow: '0 8px 24px rgba(15, 25, 35, 0.12)',
  maxWidth: 440,
  margin: '0 auto',
};

const btnGhost: React.CSSProperties = {
  padding: '8px 12px',
  background: 'transparent',
  border: '1px solid #e2e8f0',
  borderRadius: 8,
  color: '#64748b',
  fontSize: 12,
  fontWeight: 600,
  cursor: 'pointer',
  fontFamily: 'inherit',
};

const btnPrimary: React.CSSProperties = {
  padding: '8px 14px',
  background: '#3c7c9c',
  border: 'none',
  borderRadius: 8,
  color: '#ffffff',
  fontSize: 12,
  fontWeight: 700,
  cursor: 'pointer',
  fontFamily: 'inherit',
};
